import { addWeeks, addMonths, getDaysInMonth } from 'date-fns';

export type RecurrencePattern = 'weekly' | 'biweekly' | 'monthly';

/**
 * Derive which weekday occurrence (1st-5th, last) a date represents.
 * Uses UTC methods to avoid timezone issues.
 */
export function getWeekdayOccurrence(date: Date): {
  weekday: number;
  occurrence: number;
  isLast: boolean;
} {
  const dayOfMonth = date.getUTCDate();
  const weekday = date.getUTCDay();
  const occurrence = Math.ceil(dayOfMonth / 7);
  const daysInMonth = getDaysInMonth(date);
  const isLast = dayOfMonth + 7 > daysInMonth;
  return { weekday, occurrence, isLast };
}

/**
 * Find the Nth weekday in a given month. Returns null if that occurrence doesn't exist.
 */
function nthWeekdayInMonth(
  year: number,
  month: number,
  weekday: number,
  occurrence: number,
  isLast: boolean
): Date | null {
  if (isLast) {
    const lastDay = new Date(Date.UTC(year, month, 0));
    const d = lastDay.getUTCDay();
    const offset = (d - weekday + 7) % 7;
    return new Date(Date.UTC(year, month - 1, lastDay.getUTCDate() - offset));
  }
  const firstDay = new Date(Date.UTC(year, month - 1, 1));
  const d = firstDay.getUTCDay();
  const offset = (weekday - d + 7) % 7;
  const dayOfMonth = 1 + offset + (occurrence - 1) * 7;
  const daysInMonth = getDaysInMonth(new Date(Date.UTC(year, month - 1, 1)));
  if (dayOfMonth > daysInMonth) return null;
  return new Date(Date.UTC(year, month - 1, dayOfMonth));
}

/**
 * Generate preview dates for the recurrence form. Returns formatted YYYY-MM-DD strings.
 * Parses startDateStr manually to avoid timezone coercion (mirrors backend toUTCMidnight pattern).
 */
export function generatePreviewDates(
  pattern: RecurrencePattern,
  startDateStr: string,
  count = 5
): string[] {
  const [y, m, d] = startDateStr.split('-').map(Number);
  if (!y || !m || !d) return [];
  let cursor = new Date(Date.UTC(y, m - 1, d));
  const dates: string[] = [];

  // For monthly, derive occurrence info from the start date
  const startOccurrence = pattern === 'monthly' ? getWeekdayOccurrence(cursor) : null;

  while (dates.length < count) {
    dates.push(cursor.toISOString().split('T')[0]!);
    if (pattern === 'weekly') {
      cursor = addWeeks(cursor, 1);
    } else if (pattern === 'biweekly') {
      cursor = addWeeks(cursor, 2);
    } else {
      // Monthly: advance and find same weekday occurrence
      let nextMonth = addMonths(cursor, 1);
      let next = nthWeekdayInMonth(
        nextMonth.getUTCFullYear(),
        nextMonth.getUTCMonth() + 1,
        startOccurrence!.weekday,
        startOccurrence!.occurrence,
        startOccurrence!.isLast
      );
      // Skip months where the occurrence doesn't exist (e.g. 5th Tuesday)
      while (!next) {
        nextMonth = addMonths(nextMonth, 1);
        next = nthWeekdayInMonth(
          nextMonth.getUTCFullYear(),
          nextMonth.getUTCMonth() + 1,
          startOccurrence!.weekday,
          startOccurrence!.occurrence,
          startOccurrence!.isLast
        );
      }
      cursor = next;
    }
  }
  return dates;
}

const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const OCCURRENCE_LABELS = ['', '1st', '2nd', '3rd', '4th', '5th'];

/**
 * Generate a human-readable label for the monthly occurrence.
 * e.g. "Monthly on the 2nd Tuesday" or "Monthly on the last Thursday"
 */
export function getWeekdayOccurrenceLabel(startDateStr: string): string {
  const [y, m, d] = startDateStr.split('-').map(Number);
  if (!y || !m || !d) return '';
  const date = new Date(Date.UTC(y, m - 1, d));
  const { weekday, occurrence, isLast } = getWeekdayOccurrence(date);
  const weekdayName = WEEKDAY_NAMES[weekday]!;
  const occurrenceLabel = isLast ? 'last' : OCCURRENCE_LABELS[occurrence]!;
  return `Monthly on the ${occurrenceLabel} ${weekdayName}`;
}
